// eslint-disable-next-line import/default
import Highcharts from "highcharts";
/**
 * Given a set of highcharts data and some config options, return a valid
 * Highcharts config.
 *
 * Written as an ES5 module that will run in Node or a browser without
 * compilation.
 *
 * Usage in node:
 *
 * const hc = require('./highchartsConfigurator')
 * hc.getConfig(options, groupId)
 *
 * Usage in a browser:
 *
 * <script src="highchartsConfigurator.js"></script>
   <script>
    console.log(highchartsConfigurator.getConfig(options, groupId));
   </script>
 */

(function(exports) {
  const CHART_TYPES = ["bar", "column", "line", "pie", "column_line"];

  const defaultConfig = {
    chart: { type: "bar", colorCount: 13 },
    title: false,
    plotOptions: {
      bar: {
        stacking: false,
        dataLabels: {
          padding: 2,
          enabled: true
        }
      },
      column: {
        stacking: false,
        dataLabels: {
          padding: 2,
          enabled: true
        }
      },
      line: {
        dataLabels: {
          padding: 2,
          enabled: true
        },
        zIndex: 100
      },
      pie: {
        dataLabels: {
          padding: 2,
          enabled: true
        }
      },
      series: {
        dataLabels: {
          padding: 2,
          enabled: true,
          format: "{point.options.labelText}"
        }
      }
    },
    navigation: {
      buttonOptions: {
        enabled: false
      }
    },
    legend: {
      align: "left",
      padding: 15
    },
    yAxis: {
      endOnTick: false,
      title: { text: "" },
      visible: true,
      lineWidth: 10,
      maxPadding: 0.15,
      labels: {
        distance: -20,
        padding: 2,
        enabled: false,
        autoRotation: [-45, -90],
        x: -5
      }
    },
    series: [],
    xAxis: {
      categories: [],
      labels: {
        distance: -20,
        padding: 2,
        autoRotation: [-45, -90],
        y: 16
      }
    },
    tooltip: {
      valuePrefix: "",
      valueSuffix: "",
      pointFormat:
        '<span class="highcharts-color-{point.colorIndex}">\u25CF</span> {series.name}: <b>{point.labelText}</b><br/>'
    },
    credits: { enabled: false }
  };

  exports.getConfig = function(options, groupId) {
    const type = options.type || CHART_TYPES[0];
    const stacking = options.stacking || false;
    const yAxisLabel = options.yAxisLabel || "";
    const suffix = options.suffix || "";
    const prefix = options.prefix || "";
    const groups = options.groups || [];

    // No labels under 400px
    if (window.innerWidth <= 400) {
      options.disableDataLabels = true;
    }

    const currentGroup = groups.find(group => group.id === groupId);

    // Poor man's deep copy
    const configCopy = JSON.parse(JSON.stringify(defaultConfig));

    // Assign options to default config
    const config = Object.assign({}, configCopy, {
      series: currentGroup.series
    });

    config.chart.type = type;
    config.xAxis.categories = currentGroup.categories;
    config.yAxis.title.text = yAxisLabel;
    config.yAxis.labels.enabled = !!yAxisLabel;
    config.tooltip.valuePrefix = prefix;
    config.tooltip.valueSuffix = suffix;
    config.plotOptions.bar.stacking = stacking;
    config.plotOptions.column.stacking = stacking;

    if (stacking) {
      config.tooltip.shared = true;

      if (config.series.length >= 3) {
        config.plotOptions.bar.dataLabels.enabled = false;
        config.plotOptions.column.dataLabels.enabled = false;
        config.plotOptions.series.dataLabels.enabled = false;
      }
    }

    if (options.type === "bar") {
      // reset our default xAxis y label position (xAxis is vertical on bar charts)
      config.xAxis.labels.y = null;

      // Change aspect ratio to give extra height to bar charts with a lot of categories
      if (currentGroup.categories.length > 5) config.chart.height = "100%"; // 1:1 aspect ratio
    }

    if (options.type === "line") {
      // Use orange as the first series colour, then use high contrast colours
      config.series = config.series.map((serie, index) => {
        let colorIndex;
        switch (index) {
          case 0:
            colorIndex = 9;
            break;
          case 1:
            colorIndex = 0;
            break;
          case 2:
            colorIndex = 2;
            break;
          default:
            colorIndex = index + 1;
            break;
        }
        serie.colorIndex = colorIndex;

        // Set proper padding on the data labels
        serie.dataLabels = { y: -8 };
        return serie;
      });
    }

    if (options.type === "column_line") {
      // Delete existing chart type, type is specified within the series
      delete config.chart.type;

      // Setup second axis and axis labels
      const yAxis = Object.assign({}, config.yAxis);
      const yAxisDeepCopy = JSON.parse(JSON.stringify(config.yAxis)); // Note that this will fail for any unserializable fields
      const yAxis2 = Object.assign({}, yAxisDeepCopy, {
        opposite: true, // put this axis on the other side
        labels: { x: 1 } // undo negative x, so labels don't overlap the axis
      });
      yAxis2.title.text = options.yAxisLabel2;
      config.yAxis = [yAxis, yAxis2];

      config.series = config.series.map(serie => {
        if (serie.type === "line") {
          // For the first series (the line) set a custom colour and zIndex
          serie.colorIndex = 6;
          // Set custom options for the line data labels
          serie.dataLabels = {
            padding: 4,
            y: -8
          };
        }
        return serie;
      });
    }

    if (options.type === "pie") {
      // Different label formatting for pie charts
      config.plotOptions.pie.dataLabels.format =
        "<b>{point.name}</b>: {point.percentage:.1f} %";
      config.tooltip.pointFormat =
        "{series.name}: <b>{point.percentage:.1f}%</b> / <b>{point.labelText}</b>";
      config.series = [config.series[0]];
      config.series.colorByPoint = true;
    }

    // Modify series data for all chart types
    config.series = config.series.map(serie => {
      // Protect against null values (they'll crash Highcharts)
      if (serie.type === null) delete serie.type;
      if (serie.yAxis === null) delete serie.yAxis;
      if (serie.zIndex === null) delete serie.zIndex;

      // apply the disableDataLabels option
      if (options.disableDataLabels) serie.dataLabels = false;

      // Allow string data labels, set value to 0 if so.
      serie.data = serie.data.map(point => {
        if (typeof point !== "object") {
          return {
            y: typeof point === "string" ? 0 : point,
            labelText:
              typeof point === "string"
                ? point
                : Highcharts.numberFormat(point, -1, ".", ",")
          };
        } else {
          return point;
        }
      });

      return serie;
    });
    return config;
  };
})(
  typeof exports === "undefined"
    ? (this["highchartsConfigurator"] = {})
    : exports
);



// WEBPACK FOOTER //
// ./src/utils/highchartsConfigurator.js